import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { VddDetailsListStore } from '@store';
import { TranslateService } from '@ngx-translate/core';
import { RemoteDiagnosticsVddGenericStructure } from '@vi-iot/client';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import {
  DataKeyValues,
  DopnameValues,
  TypeValues,
  SemanticValues,
} from '../../../diagnostics-constants';
import { DiagnosticsHelpers } from '../../../diagnostics-helpers';
import {
  createVddEnumReadonlyNoIntervalComponent,
  createVddFalseTrueReadonlyComponent,
  createVddNumberReadonlyComponent,
  createVddTextReadonlyComponent,
} from './diagnostics-dynamic-metadata-helper';
@Component({
  selector: 'app-diagnostics-dynamic-metadata-component',
  templateUrl: './diagnostics-dynamic-metadata-component.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsDynamicMetadataComponentComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dynamicMetadataComponentContainer', { read: ViewContainerRef })
  metadataContainer: ViewContainerRef;
  @Input() vddName: string;
  @Input() logicalLink: string;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private vddDetailsListStore: VddDetailsListStore,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.vddDetailsListStore
      .getVddFlattenedMetadata(this.vddName, this.logicalLink)
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        filter((flattenedMetadata) => !!flattenedMetadata),
        distinctUntilChanged(isEqual)
      )
      .subscribe((flattenedMetadata) => {
        this.metadataContainer.clear();

        flattenedMetadata.forEach((metadata) => {
          this.createVddMetadataTemplate(metadata);
        });
        this.cdr.markForCheck();
      });
  }

  private createVddMetadataTemplate(metadata: RemoteDiagnosticsVddGenericStructure): void {
    if (metadata?.semantic === SemanticValues.RESERVED) {
      return null;
    } else if (
      DiagnosticsHelpers.includesElementsInKey(metadata, DataKeyValues.Dopname, [
        DopnameValues.FalseTrue,
      ])
    ) {
      createVddFalseTrueReadonlyComponent(
        this.metadataContainer,
        metadata?.value,
        this.translateService.instant(metadata?.translationid)
      );
    } else if (
      DiagnosticsHelpers.includesElementsInKey(metadata, DataKeyValues.Type, [
        TypeValues.INT32,
        TypeValues.FLOAT32,
      ])
    ) {
      createVddNumberReadonlyComponent(
        this.metadataContainer,
        metadata?.value,
        this.translateService.instant(metadata?.translationid),
        metadata?.unit
      );
    } else if (
      DiagnosticsHelpers.includesElementsInKey(metadata, DataKeyValues.Semantic, [
        SemanticValues.DEFVALUE,
        SemanticValues.DEFLEVEL,
        SemanticValues.ENUMCHOICE,
      ]) &&
      metadata?.structure
    ) {
      createVddEnumReadonlyNoIntervalComponent(
        this.metadataContainer,
        metadata?.value,
        this.translateService.instant(metadata?.translationid)
      );
    } else {
      createVddTextReadonlyComponent(
        this.metadataContainer,
        metadata?.value,
        metadata?.translationid ? this.translateService.instant(metadata?.translationid) : '',
        metadata?.semantic === SemanticValues.DESCRIPTION
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
